import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import type { PublicProperty } from "@shared/schema";
import { useBooking } from "@/hooks/use-booking-context";
import { rateParts } from "@/components/property/rate-line";

const img = (src: string, w: number) => `/img?src=${encodeURIComponent(src)}&w=${w}&q=90`;

/**
 * A one-tap way from one home to the other, carrying the chosen dates.
 *
 * Sits under the booking widget on each listing and booking page. When the
 * dates a guest wants are taken here, the other home is the next best
 * answer. View Property opens its details; Book Now carries the same dates.
 */
export function OtherHomeSwitch({ currentSlug, className = "" }: { currentSlug?: string | null; className?: string }) {
  const { data: homes = [] } = useQuery<PublicProperty[]>({ queryKey: ["/api/properties"] });
  const { checkIn, checkOut } = useBooking();
  const other = homes.find((h) => h.slug && h.slug !== currentSlug);
  if (!other || !other.slug) return null;

  const cover = other.hero_image_url || [...(other.photos || [])].sort((a, b) => a.sort_order - b.sort_order)[0]?.url;
  const params = new URLSearchParams();
  if (checkIn) params.set("start", checkIn);
  if (checkOut) params.set("end", checkOut);
  const qs = params.toString();
  const href = `/properties/${other.slug}/book${qs ? `?${qs}` : ""}`;
  const { rate, promo } = rateParts(other);
  const hasDates = Boolean(checkIn && checkOut);

  return (
    <aside className={`border border-sand-deep bg-sand p-4 text-center ${className}`} aria-label="Our other home" data-testid="other-home-switch">
      <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-driftwood">
        {hasDates ? "Dates not working here?" : "Our other home"}
      </p>
      <Link href={`/properties/${other.slug}`} className="group mt-3 flex flex-col items-center gap-4" data-testid="other-home-link">
        {cover && (
          <img
            src={img(cover, 240)}
            alt={other.title}
            className="h-16 w-20 shrink-0 object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
            decoding="async"
          />
        )}
        <span className="min-w-0 flex-1">
          <span className="block font-serif text-[18px] leading-tight text-ink group-hover:text-ink/70">{other.title}</span>
          <span className="mt-1 block font-sans text-[12px] text-ink/70">
            Sleeps {other.max_guests}
            {rate ? ` · from $${(promo ?? rate).toLocaleString("en-US")} / night` : ""}
          </span>
          <span className="mt-1 inline-flex items-center gap-1 font-sans text-[11px] uppercase tracking-[0.18em] text-forest">
            View property details
            <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5" />
          </span>
        </span>
      </Link>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        <Link href={`/properties/${other.slug}`} aria-label={`View Property: ${other.title}`} className="inline-flex min-h-11 items-center rounded border border-ink bg-ink px-3 font-sans text-sm text-shell hover:bg-ink/90">View Property</Link>
        <Link href={href} aria-label={`Book Now: ${other.title}`} className="inline-flex min-h-11 items-center rounded border border-ink px-3 font-sans text-sm text-ink hover:bg-shell">Book Now</Link>
      </div>
    </aside>
  );
}
